import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { appEnvironment } from '../../../common/environment';
import { useLoggedOutHeader } from '../../../headerHooks';
import { ContinueRef } from '../../../onboarding/types';
import { onboardingActions } from '../../../redux/onboarding';
import { OnboardingV2Step } from '../../../redux/onboardingTypes';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { useAppTransitions } from '../../../ui';

export const useLoggedOutLogic = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const { buttonEnabled, onboardingStep, showingMessages } = useAppSelector(
    (state) => state.onboarding
  );
  const loading = useAppSelector((state) => state.checkout.loading);

  const leftPanelImageRef = useRef<HTMLImageElement>(null);
  const formRef = useRef<ContinueRef>(null);
  const hasRunAnimation = useRef(false);

  const { leftPanelTranslationX, animatedRightPanelInOnboarding } =
    useAppTransitions(leftPanelImageRef, true);
  const headerData = useLoggedOutHeader();

  const stepTitle = useMemo(() => {
    switch (onboardingStep) {
      case OnboardingV2Step.SIGN_UP:
        return t('completeTheFieldsBelow').replace(
          /syd/gi,
          appEnvironment.theme.appName
        );
      case OnboardingV2Step.SECOND_FORM:
        return t('yourDateOfBirth');
      case OnboardingV2Step.DELIVERY_ADDRESS:
        return t('whereWouldWeShipYourGeneticsTestingKit');
      default:
        return undefined;
    }
  }, [onboardingStep, t]);

  const isAnimatingPhase = [
    OnboardingV2Step.ANIMATING,
    OnboardingV2Step.INTRO,
    OnboardingV2Step.WELCOME,
  ].includes(onboardingStep);

  useEffect(() => {
    if (
      !appEnvironment.theme.showOnboardingAnimation &&
      onboardingStep === OnboardingV2Step.ANIMATING
    ) {
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.SIGN_UP));
    }
  }, [onboardingStep, dispatch]);

  useEffect(() => {
    if (
      !hasRunAnimation.current &&
      onboardingStep === OnboardingV2Step.WELCOME
    ) {
      hasRunAnimation.current = true;
      animatedRightPanelInOnboarding();
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.INTRO));
    }
  }, [animatedRightPanelInOnboarding, dispatch, onboardingStep]);

  const handleContinue = () => {
    if (onboardingStep !== OnboardingV2Step.PAYMENT_INFO) {
      dispatch(onboardingActions.setButtonEnabled(false));
    }
    if (formRef.current) {
      formRef.current.continuePressed();
    }
  };

  return {
    t,
    searchParams,
    onboardingStep,
    buttonEnabled,
    loading,
    showingMessages,
    isAnimatingPhase,
    leftPanelImageRef,
    formRef,
    leftPanelTranslationX,
    headerData,
    stepTitle,
    handleContinue,
  };
};
